import { useState } from "react";
import { canManageAnnouncements, canReadOwnerSurveys } from "@bitacorapro/api-types";
import { Link, useLocation, useMatch, useNavigate } from "react-router-dom";
import { roleLabelFor } from "../lib/admin/words";
import { useCarteraProyectoDetail, useMyOrganization, useRecipe } from "../lib/api/queries";
import { hasAdminModule } from "../lib/capabilities";
import { logout } from "../lib/auth/actions";
import { orgDisplayName, orgMarkCandidates } from "../lib/orgMarkLogo";
import { useSession } from "../lib/auth/useSession";
import {
  hasContextualProjectNav,
  navGroupLabel,
  navItemBasePath,
  useVisibleNavItems,
  type NavGroup,
  type VisibleNavItem,
} from "../lib/nav";
import { pendientesDeGestion } from "../lib/postventa/phase";
import { Icon } from "./icons";
import { Skel } from "./Skel";

// The nav vocabulary (NAV_ITEMS), its gating and its badges live in
// ../lib/nav — shared with CommandPalette and RouteAnnouncer so the
// three surfaces always agree on which screens this org and role see.
//
// The contextual project block (panel postventa v2, README §Navegación)
// is Sidebar-only: inside /proyectos/:id it replaces the Proyectos entry
// with a back link, the project's name and sub-line, and the four
// project-scoped links (Unidades / Observaciones / Consultas / Archivo —
// Consultas since ADR-0145). It only exists for a vertical with the
// observation lifecycle (lib/nav's hasContextualProjectNav) — facility
// renders exactly as before, whatever the path looks like.

function initialsFor(name: string): string {
  return (
    name
      .split(/\s+/)
      .filter(Boolean)
      .slice(0, 2)
      .map((w) => w.charAt(0).toUpperCase())
      .join("") || "?"
  );
}

// The org square (ADR-0164). `candidates` is the server-shaped chain
// from lib/orgMarkLogo (caller's brand → org logo → default brand); the
// first one that loads wins, a failing image hands over to the next, and
// the initials stay as the last fallback (when nothing has a logo, or
// when every image fails). The broken set resets
// whenever the chain's content changes, so a newly uploaded logo gets its
// chance even if an earlier URL failed.
const NO_BROKEN: ReadonlySet<string> = new Set();

function OrgMark({ name, candidates }: { name: string; candidates: readonly string[] }) {
  const signature = candidates.join("\n");
  const [state, setState] = useState<{ signature: string; broken: ReadonlySet<string> }>({
    signature,
    broken: NO_BROKEN,
  });
  const broken = state.signature === signature ? state.broken : NO_BROKEN;
  const src = candidates.find((c) => !broken.has(c));
  if (src) {
    return (
      <div className="sidebar__org-mark sidebar__org-mark--logo">
        <img
          key={src}
          src={src}
          alt=""
          decoding="async"
          onError={() =>
            setState((prev) => {
              const base = prev.signature === signature ? prev.broken : NO_BROKEN;
              const next = new Set(base);
              next.add(src);
              return { signature, broken: next };
            })
          }
        />
      </div>
    );
  }
  return (
    <div className="sidebar__org-mark" aria-hidden="true">
      {initialsFor(name)}
    </div>
  );
}

function isActive(pathname: string, item: VisibleNavItem): boolean {
  // Una entrada puede llevar querystring: lo activo se decide por la ruta.
  const base = navItemBasePath(item);
  if (base === "/") return pathname === "/";
  return pathname === base || pathname.startsWith(`${base}/`);
}

function linkClass(active: boolean, alert = false): string {
  return [
    "sidebar__link",
    active ? "sidebar__link--active" : "",
    alert && !active ? "sidebar__link--alert" : "",
  ]
    .filter(Boolean)
    .join(" ");
}

// An accordion parent (Administración, plan 2026-10-30 §1.5): a link to
// its own page and, below it, its children. The accordion follows the
// route, not a click — it is open while the current path is the parent's
// or one of its children's. The parent carries `--active` only on its own
// page (exact match; its prefix covers every child), `--open` when a
// child is the current page. The list stays in the DOM while closed
// (hidden) so aria-controls always points at an element.
function NavAccordion({
  parent,
  items,
  pathname,
}: {
  parent: VisibleNavItem;
  items: VisibleNavItem[];
  pathname: string;
}) {
  const IconC = Icon[parent.icon];
  const parentBase = navItemBasePath(parent);
  const parentActive = pathname === parentBase;
  // A child on the parent's own path (Resumen, once it exists) is active
  // only there, not under every sibling by prefix.
  const childIsActive = (c: VisibleNavItem) =>
    navItemBasePath(c) === parentBase ? parentActive : isActive(pathname, c);
  const childActive = items.some((c) => navItemBasePath(c) !== parentBase && childIsActive(c));
  const open = parentActive || childActive;
  const subId = `sidebar-sub-${parent.key}`;
  return (
    <>
      <Link
        to={parent.path}
        title={`Abrir ${parent.label.toLowerCase()}`}
        aria-current={parentActive ? "page" : undefined}
        aria-expanded={open}
        aria-controls={subId}
        className={[linkClass(parentActive), open && !parentActive ? "sidebar__link--open" : ""]
          .filter(Boolean)
          .join(" ")}
      >
        <IconC />
        <span>{parent.label}</span>
        {parent.count != null && <span className="sidebar__link-count">{parent.count}</span>}
        <span className="sidebar__caret" aria-hidden="true">
          <Icon.ChevronRight s={12} />
        </span>
      </Link>
      <ul id={subId} className="sidebar__sub" hidden={!open}>
        {items.map((c) => {
          const active = childIsActive(c);
          // La portada del módulo (Resumen) es la primera hija y la misma
          // página que el padre: va en 600 (handoff, `--home`).
          const home = navItemBasePath(c) === parentBase;
          return (
            <li key={c.key}>
              <Link
                to={c.path}
                title={`Abrir ${c.label.toLowerCase()}`}
                aria-current={active ? "page" : undefined}
                className={[
                  "sidebar__sublink",
                  home ? "sidebar__sublink--home" : "",
                  active ? "sidebar__sublink--active" : "",
                ]
                  .filter(Boolean)
                  .join(" ")}
              >
                <span className="sidebar__subdot" aria-hidden="true" />
                <span>{c.label}</span>
                {c.count != null && <span className="sidebar__link-count">{c.count}</span>}
              </Link>
            </li>
          );
        })}
      </ul>
    </>
  );
}

function NavGroupBlock({
  group,
  items,
  pathname,
  label,
}: {
  group: NavGroup;
  items: VisibleNavItem[];
  pathname: string;
  // Explicit `null` renders the group without a heading (the lone
  // Proyectos entry of the postventa sidebar, as the handoff shows it).
  label: string | null;
}) {
  if (items.length === 0) return null;
  // Children are listed flat by lib/nav (⌘K and the route announcer read
  // them as plain entries); here they go under their parent.
  const topLevel = items.filter((n) => n.parentKey === undefined);
  return (
    <div className="sidebar__group" data-group={group}>
      {label != null && <div className="sidebar__group-label">{label}</div>}
      {topLevel.map((n) => {
        const children = items.filter((c) => c.parentKey === n.key);
        if (children.length > 0) {
          return <NavAccordion key={n.key} parent={n} items={children} pathname={pathname} />;
        }
        const IconC = Icon[n.icon];
        const active = isActive(pathname, n);
        const count = n.count;
        // The badge is a findings total, not a queue, so the link's
        // accessible name stays the plain label and the count reads
        // as its own text (accessibility-expert, sidebar finding).
        return (
          <Link
            key={n.key}
            to={n.path}
            aria-current={active ? "page" : undefined}
            className={linkClass(active, n.alert)}
          >
            <IconC />
            <span>{n.label}</span>
            {count != null && <span className="sidebar__link-count">{count}</span>}
          </Link>
        );
      })}
    </div>
  );
}

// The project-scoped rail. The name is a skeleton while the detail
// loads (never an empty heading that flashes in); on error the block
// keeps the back link and the three links, which need only the id.
function ProjectRail({
  projectId,
  pathname,
  showAvisos,
  showEncuestas,
}: {
  projectId: string;
  pathname: string;
  /** ADR-0153: el gerente y el jefe, y sólo si la receta encuesta. */
  showEncuestas: boolean;
  /** ADR-0152: sólo los roles que gestionan avisos ven la entrada (el
   *  servidor responde 403 al resto; una acción sin capacidad no se
   *  muestra). */
  showAvisos: boolean;
}) {
  const detail = useCarteraProyectoDetail(projectId, { limit: 1 });
  const group = detail.data?.group;
  const base = `/proyectos/${projectId}`;
  const obsPath = `${base}/observaciones`;
  const consultasPath = `${base}/consultas`;
  const avisosPath = `${base}/avisos`;
  const encuestasPath = `${base}/encuestas`;
  const archivoPath = `${base}/archivo`;
  const unidadesActive = pathname === base;
  const obsActive = pathname === obsPath || pathname.startsWith(`${obsPath}/`);
  const consultasActive = pathname === consultasPath || pathname.startsWith(`${consultasPath}/`);
  const avisosActive = pathname === avisosPath || pathname.startsWith(`${avisosPath}/`);
  const encuestasActive = pathname === encuestasPath || pathname.startsWith(`${encuestasPath}/`);
  const archivoActive = pathname === archivoPath || pathname.startsWith(`${archivoPath}/`);
  // Server aggregate of what the project still has to manage — the list
  // may be filtered by unidad, the badge never is (README §Navegación).
  const pending = group ? pendientesDeGestion(group) : 0;
  // ADR-0145: consultas del propietario sin responder, del mismo rollup
  // (vencidas incluidas). Opcional en el wire — una vertical sin el canal
  // del propietario no lo emite y no hay badge.
  const consultasPendientes = group?.unansweredInquiries ?? 0;
  const unitsLabel = detail.data?.labels.assetPlural.toLowerCase() ?? "unidades";
  const subline = group
    ? [group.subline, `${group.assetCount} ${unitsLabel}`].filter(Boolean).join(" · ")
    : null;

  return (
    <div className="sidebar__group sb-proj">
      <Link to="/proyectos" className="sb-proj__back">
        <Icon.ChevronLeft />
        <span>Todos los proyectos</span>
      </Link>
      {group ? (
        <>
          <div className="sb-proj__name">{group.name}</div>
          <div className="sb-proj__sub">{subline}</div>
        </>
      ) : detail.isError ? null : (
        // The rail never announces (the route owns the page's one
        // role=status): the loading block is only marked busy. A plain
        // block wrapper, so the name and sub-line keep their spacing.
        <div aria-busy="true" data-skeleton="section">
          <div className="sb-proj__name">
            <Skel width="82%" height={16} />
          </div>
          <div className="sb-proj__sub">
            <Skel width="55%" height={11} />
          </div>
        </div>
      )}
      <Link
        to={base}
        aria-current={unidadesActive ? "page" : undefined}
        className={linkClass(unidadesActive)}
      >
        <Icon.Units />
        <span>Unidades</span>
      </Link>
      <Link
        to={obsPath}
        aria-current={obsActive ? "page" : undefined}
        className={linkClass(obsActive)}
      >
        <Icon.Inbox />
        <span>Observaciones</span>
        {pending > 0 && (
          <span className="sidebar__link-count">
            {pending}
            <span className="sr-only"> pendientes de gestión</span>
          </span>
        )}
      </Link>
      <Link
        to={consultasPath}
        aria-current={consultasActive ? "page" : undefined}
        className={linkClass(consultasActive)}
      >
        <Icon.Mail />
        <span>Consultas</span>
        {consultasPendientes > 0 && (
          <span className="sidebar__link-count">
            {consultasPendientes}
            <span className="sr-only"> sin responder</span>
          </span>
        )}
      </Link>
      {/* ADR-0152 — avisos y guías de mantención del proyecto. */}
      {showAvisos && (
        <Link
          to={avisosPath}
          aria-current={avisosActive ? "page" : undefined}
          className={linkClass(avisosActive)}
        >
          <Icon.Bell />
          <span>Avisos</span>
        </Link>
      )}
      {/* ADR-0153 — encuestas del propietario. */}
      {showEncuestas && (
        <Link
          to={encuestasPath}
          aria-current={encuestasActive ? "page" : undefined}
          className={linkClass(encuestasActive)}
        >
          <Icon.Survey />
          <span>Encuestas</span>
        </Link>
      )}
      <Link
        to={archivoPath}
        aria-current={archivoActive ? "page" : undefined}
        className={linkClass(archivoActive)}
      >
        <Icon.Archive />
        <span>Archivo</span>
      </Link>
    </div>
  );
}

export function Sidebar() {
  const session = useSession();
  const { data: recipe } = useRecipe();
  const { pathname } = useLocation();
  const navigate = useNavigate();
  const visibleNavItems = useVisibleNavItems();
  // Hooks are unconditional: both patterns are matched every render and
  // the id is picked afterwards (`/proyectos/:id/*` also covers the bare
  // detail route in react-router v6, the second match is the belt).
  const matchNested = useMatch("/proyectos/:id/*");
  const matchExact = useMatch("/proyectos/:id");
  const projectId = matchNested?.params.id ?? matchExact?.params.id ?? null;
  const contextualNav = hasContextualProjectNav(recipe);
  const railProjectId = contextualNav ? projectId : null;

  async function handleLogout() {
    await logout();
    navigate("/login", { replace: true });
  }

  const myOrg = useMyOrganization();
  // The session only carries the legal org's name; a brand-scoped caller's
  // identity (callerBrand) arrives with GET /organizations/me. Until it
  // does, no name is asserted — showing the session's would flash «FORUS»
  // at a Rockford supervisor. On error it falls back to the org name, like
  // the logo.
  const identityPending = myOrg.isPending === true;
  const orgName = orgDisplayName(myOrg.data, session?.organization.name);
  const userName = session?.user.displayName ?? "Sin sesión";
  const role = session?.organization.role;
  const roleLabel = roleLabelFor(role, recipe);

  const byGroup = (group: NavGroup) => visibleNavItems.filter((n) => n.group === group);

  return (
    <aside className="sidebar">
      <div className="sidebar__brand">
        <span className="sidebar__brand-name">BitácoraPro</span>
      </div>

      {/* Busy while the identity loads, never a role=status: the
          sidebar is chrome, the route announces its own loading. */}
      <div className="sidebar__org" aria-busy={identityPending || undefined}>
        {identityPending ? (
          <div className="sidebar__org-mark" aria-hidden="true" />
        ) : (
          <OrgMark name={orgName} candidates={orgMarkCandidates(myOrg.data)} />
        )}
        <div className="sidebar__org-meta">
          <div className="sidebar__org-name">
            {identityPending ? <Skel width="60%" height={14} /> : orgName}
          </div>
        </div>
      </div>

      {/* Compact spacing only for a recipe with the Administración module:
          its open accordion must fit 768px of height without scrolling
          (handoff §Navegación). Every other vertical keeps its spacing. */}
      <nav
        className={hasAdminModule(recipe) ? "sidebar__nav sidebar__nav--compact" : "sidebar__nav"}
      >
        {railProjectId != null ? (
          <ProjectRail
            projectId={railProjectId}
            pathname={pathname}
            showAvisos={role !== undefined && canManageAnnouncements(role)}
            showEncuestas={
              role !== undefined && canReadOwnerSurveys(role) && recipe?.ownerSurveys !== undefined
            }
          />
        ) : (
          <NavGroupBlock
            group="principal"
            items={byGroup("principal")}
            pathname={pathname}
            label={contextualNav ? null : navGroupLabel("principal")}
          />
        )}

        <NavGroupBlock
          group="cartera"
          items={byGroup("cartera")}
          pathname={pathname}
          label={navGroupLabel("cartera")}
        />

        <NavGroupBlock
          group="gestion"
          items={byGroup("gestion")}
          pathname={pathname}
          label={navGroupLabel("gestion")}
        />

        {session?.user.platformAdmin ? (
          <div className="sidebar__group">
            <div className="sidebar__group-label">Plataforma</div>
            <Link
              to="/admin"
              aria-current={pathname.startsWith("/admin") ? "page" : undefined}
              className={linkClass(pathname.startsWith("/admin"))}
            >
              <Icon.Settings />
              <span>Panel interno</span>
            </Link>
          </div>
        ) : null}
      </nav>

      <div className="sidebar__foot">
        <div className="sidebar__avatar">{initialsFor(userName)}</div>
        <div className="sidebar__user">
          <div className="sidebar__user-name">{userName}</div>
          <div className="sidebar__user-role">{roleLabel}</div>
        </div>
        <button
          type="button"
          className="sidebar__user-action"
          title="Cerrar sesión"
          aria-label="Cerrar sesión"
          onClick={() => void handleLogout()}
        >
          <Icon.LogOut s={16} />
        </button>
      </div>
    </aside>
  );
}
